import { MULTIPLAYER_STATE_VERSION } from '../engine/multiplayer/MultiplayerState.js';

function assertMethod(value, method, name) {
  if (!value || typeof value[method] !== 'function') {
    throw new TypeError(`${name} must provide ${method}()`);
  }
}

export function installMultiplayerTestApi({
  baseApi,
  multiplayerState,
  storySystem,
  bus,
  clock,
  simulationSnapshots,
  getLocalPlayerId,
  setLocalPlayerId,
  windowRef = globalThis.window,
}) {
  if (!baseApi || typeof baseApi !== 'object') throw new TypeError('baseApi must be an object');
  assertMethod(multiplayerState, 'createSession', 'multiplayerState');
  assertMethod(multiplayerState, 'snapshot', 'multiplayerState');
  assertMethod(storySystem, 'choose', 'storySystem');
  assertMethod(bus, 'emit', 'bus');
  assertMethod(clock, 'snapshot', 'clock');
  assertMethod(simulationSnapshots, 'getStatus', 'simulationSnapshots');
  if (typeof getLocalPlayerId !== 'function') throw new TypeError('getLocalPlayerId must be a function');
  if (typeof setLocalPlayerId !== 'function') throw new TypeError('setLocalPlayerId must be a function');

  const timing = (options = {}) => ({
    tick: options.tick ?? simulationSnapshots.getStatus().nextTick,
    simulationTime: options.simulationTime ?? clock.snapshot().simulationTime,
  });

  const emitChange = (operation, value = null) => {
    const status = multiplayerState.getStatus();
    bus.emit('multiplayer-state:changed', { operation, value, status });
    return status;
  };

  const assertSnapshotTiming = (snapshot) => {
    const parsed = typeof snapshot === 'string' ? JSON.parse(snapshot) : snapshot;
    const nextTick = simulationSnapshots.getStatus().nextTick;
    if (parsed?.tick !== null && parsed?.tick !== undefined && parsed.tick >= nextTick) {
      throw new RangeError('Multiplayer snapshot must precede the next simulation tick');
    }
  };

  const baseCaptureSnapshot = typeof baseApi.captureSnapshot === 'function'
    ? baseApi.captureSnapshot.bind(baseApi)
    : null;

  const api = Object.freeze({
    ...baseApi,
    multiplayerStateVersion: MULTIPLAYER_STATE_VERSION,
    getMultiplayerStatus: () => multiplayerState.getStatus(),
    getMultiplayerDiagnostics: () => multiplayerState.getDiagnostics(),
    getMultiplayerSession: () => multiplayerState.getSession(),
    getMultiplayerLocalPlayerId: () => getLocalPlayerId(),
    setMultiplayerLocalPlayer: (playerId = null) => {
      if (playerId !== null && !multiplayerState.getPlayer(playerId)) {
        throw new RangeError(`Unknown multiplayer player: ${playerId}`);
      }
      setLocalPlayerId(playerId);
      return { ok: true, playerId: getLocalPlayerId(), status: multiplayerState.getStatus() };
    },
    createMultiplayerSession: (options) => {
      const current = timing(options);
      const state = multiplayerState.createSession({ ...options, tick: current.tick });
      setLocalPlayerId(options.hostPlayerId);
      bus.emit('multiplayer:session-create', state);
      return { ok: true, state, status: emitChange('session-create', state) };
    },
    joinMultiplayerPlayer: (options) => {
      const player = multiplayerState.joinPlayer({ ...options, tick: timing(options).tick });
      bus.emit('multiplayer:player-join', player);
      return { ok: true, player, status: emitChange('player-join', player) };
    },
    setMultiplayerPlayerReady: (playerId, ready = true, options = {}) => {
      const player = multiplayerState.setPlayerReady(playerId, ready, timing(options).tick);
      bus.emit('multiplayer:player-ready', player);
      return { ok: true, player, status: emitChange('player-ready', player) };
    },
    startMultiplayerSession: (options = {}) => {
      const state = multiplayerState.startSession({
        playerId: options.playerId ?? getLocalPlayerId(),
        tick: timing(options).tick,
      });
      bus.emit('multiplayer:session-start', state);
      return { ok: true, state, status: emitChange('session-start', state) };
    },
    endMultiplayerSession: (options = {}) => {
      const state = multiplayerState.endSession({
        playerId: options.playerId ?? getLocalPlayerId(),
        tick: timing(options).tick,
      });
      bus.emit('multiplayer:session-end', state);
      return { ok: true, state, status: emitChange('session-end', state) };
    },
    disconnectMultiplayerPlayer: (playerId, options = {}) => {
      const player = multiplayerState.disconnectPlayer(playerId, { tick: timing(options).tick });
      bus.emit('multiplayer:player-disconnect', player);
      return { ok: true, player, status: emitChange('player-disconnect', player) };
    },
    reconnectMultiplayerPlayer: (playerId, options = {}) => {
      const player = multiplayerState.reconnectPlayer(playerId, { tick: timing(options).tick });
      bus.emit('multiplayer:player-reconnect', player);
      return { ok: true, player, status: emitChange('player-reconnect', player) };
    },
    leaveMultiplayerPlayer: (playerId, options = {}) => {
      const player = multiplayerState.leavePlayer(playerId, { tick: timing(options).tick });
      if (getLocalPlayerId() === playerId) setLocalPlayerId(null);
      bus.emit('multiplayer:player-leave', player);
      return { ok: true, player, status: emitChange('player-leave', player) };
    },
    getMultiplayerPlayer: (playerId) => multiplayerState.getPlayer(playerId),
    listMultiplayerPlayers: (options = {}) => multiplayerState.listPlayers(options),
    claimMultiplayerAuthority: (options) => {
      const authority = multiplayerState.claimAuthority(options);
      bus.emit('multiplayer:authority-claim', authority);
      return { ok: true, authority, status: emitChange('authority-claim', authority) };
    },
    releaseMultiplayerAuthority: (authorityId, options = {}) => {
      const authority = multiplayerState.releaseAuthority(authorityId, options);
      if (authority) bus.emit('multiplayer:authority-release', authority);
      return { ok: Boolean(authority), authority, status: emitChange('authority-release', authority) };
    },
    getMultiplayerAuthority: (authorityId) => multiplayerState.getAuthority(authorityId),
    listMultiplayerAuthorities: () => multiplayerState.listAuthorities(),
    submitMultiplayerInput: (options) => {
      const result = multiplayerState.submitInput(options);
      bus.emit('multiplayer:input', result);
      return { ok: true, result, status: emitChange('input', result) };
    },
    getMultiplayerInputForTick: (playerId, tick) => multiplayerState.getInputForTick(playerId, tick),
    listMultiplayerInputFrames: (options = {}) => multiplayerState.listInputFrames(options),
    openMultiplayerDecision: (options) => {
      const decision = multiplayerState.openDecision(options);
      bus.emit('multiplayer:decision-open', decision);
      return { ok: true, decision, status: emitChange('decision-open', decision) };
    },
    castMultiplayerVote: (decisionId, playerId, optionId) => {
      const decision = multiplayerState.castVote(decisionId, playerId, optionId);
      bus.emit('multiplayer:decision-vote', decision);
      return { ok: true, decision, status: emitChange('decision-vote', decision) };
    },
    cancelMultiplayerDecision: (decisionId, options = {}) => {
      const decision = multiplayerState.cancelDecision(decisionId, {
        playerId: options.playerId ?? getLocalPlayerId(),
      });
      bus.emit('multiplayer:decision-cancel', decision);
      return { ok: true, decision, status: emitChange('decision-cancel', decision) };
    },
    getMultiplayerDecision: (decisionId) => multiplayerState.getDecision(decisionId),
    listMultiplayerDecisions: (options = {}) => multiplayerState.listDecisions(options),
    applyResolvedMultiplayerStoryChoice: (decisionId, options = {}) => {
      const decision = multiplayerState.getDecision(decisionId);
      if (!decision) throw new RangeError(`Unknown multiplayer decision: ${decisionId}`);
      if (decision.status !== 'resolved') throw new RangeError('Multiplayer decision is not resolved');
      if (!decision.resolvedOptionId) throw new RangeError('Multiplayer decision has no resolved option');
      const storyChoiceId = decision.metadata?.storyChoiceId;
      if (typeof storyChoiceId !== 'string' || storyChoiceId.length === 0) {
        throw new RangeError('Multiplayer decision does not declare metadata.storyChoiceId');
      }
      const result = storySystem.choose(
        storyChoiceId,
        decision.resolvedOptionId,
        timing(options),
      );
      bus.emit('story:choice', result);
      bus.emit('multiplayer:story-choice', { decision, result });
      return { ok: true, decision, result, status: emitChange('story-choice', result) };
    },
    captureMultiplayerSnapshot: () => multiplayerState.snapshot(),
    exportMultiplayerSnapshot: () => multiplayerState.exportSnapshot(),
    restoreMultiplayerSnapshot: (snapshot) => {
      assertSnapshotTiming(snapshot);
      const state = multiplayerState.restore(snapshot);
      const localPlayerId = getLocalPlayerId();
      if (localPlayerId !== null && !multiplayerState.getPlayer(localPlayerId)) setLocalPlayerId(null);
      bus.emit('multiplayer:restore', state);
      return { ok: true, state, status: emitChange('restore', state) };
    },
    resetMultiplayerState: () => {
      setLocalPlayerId(null);
      const status = multiplayerState.reset();
      bus.emit('multiplayer:reset', status);
      emitChange('reset', status);
      return { ok: true, status };
    },
    captureSnapshot: baseCaptureSnapshot === null
      ? undefined
      : () => ({
        ...baseCaptureSnapshot(),
        multiplayerState: multiplayerState.snapshot(),
        multiplayerLocalPlayerId: getLocalPlayerId(),
      }),
  });

  Object.defineProperty(windowRef, '__SERPENT_TEST__', {
    configurable: true,
    enumerable: false,
    value: api,
  });

  return api;
}
